/**
 * EventHighlightsFeed — Iter-313j
 *
 * Full-width Community Feed section that replaces the legacy Creator
 * Spotlights strip. Displays approved event photos with event context
 * (title, city/state, date). Client logo only shown if the client
 * opted in (`community_logo_opt_in`).
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Sparkles, MapPin, Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import api from '../utils/api';

const relativeDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const days = Math.floor((now - d) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'Last Week';
  if (days < 30) return 'This Month';
  if (days < 60) return 'Last Month';
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const EventHighlightsFeed = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/event-highlights?limit=24');
        setItems(res.data?.items || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;
  if (items.length === 0) {
    return (
      <Card className="bg-slate-50 border-dashed" data-testid="event-highlights-empty">
        <CardContent className="p-8 text-center">
          <Sparkles className="w-8 h-8 mx-auto text-slate-400 mb-2" />
          <p className="text-sm font-medium text-slate-700 mb-1">No highlights yet</p>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">Approved event photos from talent, clients, and admins will appear here soon.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="event-highlights-feed">
      <div className="flex items-center gap-2 px-1">
        <Sparkles className="w-4 h-4 text-cyan-600" />
        <h3 className="text-sm font-semibold text-gray-900">Event Highlights</h3>
        <Badge className="bg-cyan-50 text-cyan-700 border-cyan-200 text-[10px]">Live from the field</Badge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((h) => (
          <Card key={h.id} className="overflow-hidden hover:shadow-md transition" data-testid={`event-highlight-card-${h.id}`}>
            <div className="relative aspect-[4/3] bg-slate-100">
              <img
                src={h.photo_url}
                alt={h.event_title || 'Event highlight'}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {h.client_logo_url && (
                <div className="absolute top-2 right-2 bg-white/95 rounded-md px-2 py-1 shadow flex items-center gap-1.5">
                  <img src={h.client_logo_url} alt={h.client_company_name || ''} className="h-4 w-auto" />
                </div>
              )}
            </div>
            <CardContent className="p-3">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {h.event_title || (h.role_name ? `${h.role_name} Activation` : 'Event')}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-600">
                {(h.city || h.state) && (
                  <span className="inline-flex items-center gap-0.5">
                    <MapPin className="w-3 h-3" /> {[h.city, h.state].filter(Boolean).join(', ')}
                  </span>
                )}
                {h.shift_date && (
                  <span className="inline-flex items-center gap-0.5">
                    <CalendarIcon className="w-3 h-3" /> {relativeDate(h.shift_date)}
                  </span>
                )}
                {h.role_name && (
                  <Badge variant="outline" className="text-[10px] bg-cyan-50 text-cyan-700 border-cyan-100">
                    {h.role_name}
                  </Badge>
                )}
              </div>
              {h.caption && (
                <p className="mt-2 text-[12px] text-gray-700 leading-snug line-clamp-2">{h.caption}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default EventHighlightsFeed;
